/**
 * QUICK ADD MODAL
 *
 * Natulim TS - 08-jun-26: Refactor — sistema genérico de CSS/JS para secciones
 * - loadSectionAssets(): carga <link> y <script src> del <head> del HTML fetched
 * - extractAndHoistSectionStyles(): mueve <style> inline de secciones al <head>,
 *   reemplazando selectores #shopify-section-xxx → #quickadd-shopify-section-xxx
 * - Intercept de DOMContentLoaded: captura init functions de secciones y las ejecuta
 *   después de que los scripts CDN carguen (Promise.all + onload)
 * - _sectionCleanups: destruye Swipers y remueve window listeners al cerrar modal
 *
 * Natulim SP - 19-nov-25: Fix autoplay videos
 * - initializeVideos(): fuerza play() en video[autoplay] muted re-insertados por
 *   HTMLUpdateUtility (que rompe el autoplay nativo del navegador)
 *
 * Natulim SP - 18-nov-25: Fix videos circulares
 * - circle-videos.liquid se auto-inicializa via readyState check al re-inyectarse
 * - initializeCircleVideos() crea .modalWrapperVideo si no existe y re-adjunta listeners
 */


// Natulim SP - 4-dic-25: CSS y JS específicos para productos KER
// Estos recursos se cargan dinámicamente cuando se abre un producto KER en el modal
const KER_ASSETS = {
  // Handles de productos KER (de la colección cabello-y-cuerpo)
  productHandles: ['champu', 'gel-de-ducha', 'jabon-de-manos-ker', 'dispensador-ker'],
  // Indicadores en el HTML para detectar productos KER
  htmlIndicators: ['.ker-steps-container', '.wrap-price', '.price-ker', '[class*="ker-"]'],
};

// Natulim TS - 08-jun-26: Cargar CSS/JS de assets del tema desde el HTML fetched de la PDP.
// Extrae <link stylesheet> y <script src> del <head> e inyecta los que aún no están en el doc.
function loadSectionAssets(htmlDoc) {
  htmlDoc.querySelectorAll('link[rel="stylesheet"][href*="/assets/"]').forEach(link => {
    const href = link.getAttribute('href');
    if (!href) return;
    const hrefBase = href.split('?')[0];
    const alreadyLoaded = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .some(el => el.getAttribute('href')?.split('?')[0] === hrefBase);
    if (!alreadyLoaded) {
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      document.head.appendChild(el);
    }
  });

  htmlDoc.querySelectorAll('script[src*="/assets/"]').forEach(script => {
    const src = script.getAttribute('src');
    if (!src) return;
    const srcBase = src.split('?')[0];
    const alreadyLoaded = Array.from(document.querySelectorAll('script[src]'))
      .some(el => el.getAttribute('src')?.split('?')[0] === srcBase);
    if (!alreadyLoaded) {
      const el = document.createElement('script');
      el.src = src;
      el.defer = true;
      document.head.appendChild(el);
    }
  });
}




// Función para detectar si el HTML cargado es de un producto KER
function isKerProduct(html, productUrl) {
  // Método 1: Verificar por handle del producto en la URL
  if (productUrl) {
    const handleMatch = productUrl.match(/\/products\/([^/?#]+)/);
    if (handleMatch && KER_ASSETS.productHandles.includes(handleMatch[1])) {
      return true;
    }
  }

  // Método 2: Verificar indicadores en el HTML
  if (html) {
    const parser = new DOMParser();
    const doc = typeof html === 'string' ? parser.parseFromString(html, 'text/html') : html;

    for (const indicator of KER_ASSETS.htmlIndicators) {
      if (doc.querySelector(indicator)) {
        return true;
      }
    }

    // Método 3: Verificar si el HTML contiene referencias a ker-styles.css
    if (typeof html === 'string' && html.includes('ker-styles.css')) {
      return true;
    }
  }

  return false;
}

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        // Registro de funciones de limpieza de cada apertura del modal
        this._sectionCleanups = [];

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        // Ejecutar cleanups antes de limpiar el HTML (Swiper.destroy, removeEventListener, etc.)
        this._sectionCleanups.forEach(fn => { try { fn(); } catch(e) {} });
        this._sectionCleanups = [];

        this.modalContent.innerHTML = '';

        // Limpiar <style> inline de secciones inyectados al abrir este modal
        document.querySelectorAll('style[data-quick-add-modal]').forEach(el => el.remove());

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        const productUrl = opener.getAttribute('data-product-url');

        // Natulim - 24-dic-25: Productos KER solo abren modal desde /collections/compra-y-gana
        if (isKerProduct(null, productUrl) && !window.location.pathname.includes('/collections/compra-y-gana')) {
          window.location.href = productUrl;
          return;
        }

        this.openModalWithProduct(opener, productUrl);
      }

      openModalWithProduct(opener, productUrl) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => this.processModalContent(opener, responseText))
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      processModalContent(opener, responseText) {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector('product-info');
        const allSectionsElement = responseHTML.querySelectorAll('#MainContent .shopify-section');

        // Cargar CSS/JS de assets del tema presentes en la PDP fetched
        loadSectionAssets(responseHTML);

        const productUrl = opener.getAttribute('data-product-url');
        if (isKerProduct(responseText, productUrl)) {
          this.modalContent.classList.add('ker-product-modal');
        } else {
          this.modalContent.classList.remove('ker-product-modal');
        }



        this.preprocessHTML(productElement);

        // Use HTMLUpdateUtility for proper script/custom element handling
        HTMLUpdateUtility.setInnerHTML(
          this.modalContent,
          `<div id="seccion-objetivo" style="display: block;"></div> ${productElement.outerHTML}`
        );

        // Natulim - 25-sep-26: en "compra-1-elige-1-gratis" el quick-add solo debe
        // ofrecer compra única, nunca suscripción (el script de ntl-bb-subs ya
        // corrió de forma síncrona arriba, así que el listener de click ya existe).
        if (window.location.pathname.includes('/collections/compra-1-elige-1-gratis')) {
          this.forceOnetimeOnly(this.modalContent);
        }

        // Natulim TS - 08-jun-26: Interceptar DOMContentLoaded para capturar init functions de secciones.
        // Las secciones registran sus inits en DOMContentLoaded (ya disparó). Los capturamos y
        // los ejecutamos DESPUÉS de que los scripts externos (CDN) hayan cargado.
        const domReadyCallbacks = [];
        const origAddEventListener = document.addEventListener.bind(document);
        document.addEventListener = function(type, fn, ...args) {
          if (type === 'DOMContentLoaded') { domReadyCallbacks.push(fn); return; }
          return origAddEventListener(type, fn, ...args);
        };

        allSectionsElement.forEach(section => {
          if (section.querySelector('product-info')) return;
          this.preprocessHTML(section);
          const otherSection = document.createElement('section');
          otherSection.className = section.className;
          if (section.id) otherSection.id = `quickadd-${section.id}`;
          this.modalContent.append(otherSection);
          HTMLUpdateUtility.setInnerHTML(otherSection, section.innerHTML);
        });

        document.addEventListener = origAddEventListener;

        // Ejecutar callbacks esperando a que los scripts CDN de las secciones carguen primero.
        // Después de cada init, detecta instancias Swiper y listeners window creados para limpiarlos en hide().
        const runSectionCallbacks = () => {
          // Parchar window.addEventListener temporalmente para capturar listeners registrados por las secciones
          const capturedWindowListeners = [];
          const origWindowAddListener = window.addEventListener.bind(window);
          window.addEventListener = function(type, fn, ...args) {
            capturedWindowListeners.push({ type, fn });
            return origWindowAddListener(type, fn, ...args);
          };

          // Snapshot de instancias Swiper antes de los inits
          const swipersBefore = new Set(
            Array.from(this.modalContent.querySelectorAll('.swiper')).map(el => el.swiper).filter(Boolean)
          );

          domReadyCallbacks.forEach(fn => { try { fn(); } catch(e) {} });

          window.addEventListener = origWindowAddListener;

          // Registrar cleanup: destruir instancias Swiper nuevas y remover listeners de window
          const swipersAfter = Array.from(this.modalContent.querySelectorAll('.swiper'))
            .map(el => el.swiper).filter(Boolean);
          const newSwipers = swipersAfter.filter(s => !swipersBefore.has(s));

          this._sectionCleanups.push(() => {
            newSwipers.forEach(s => { try { s.destroy(true, true); } catch(e) {} });
            capturedWindowListeners.forEach(({ type, fn }) => {
              try { window.removeEventListener(type, fn); } catch(e) {}
            });
          });
        };

        const cdnScripts = Array.from(this.modalContent.querySelectorAll('section script[src]'))
          .filter(s => s.src && !s.src.includes('/assets/'));

        if (cdnScripts.length === 0) {
          runSectionCallbacks();
        } else {
          // Para cada script CDN: si ya hay otro con el mismo src en el doc (cargado antes),
          // resolver inmediatamente; si no, esperar su evento load.
          Promise.all(cdnScripts.map(script => new Promise(resolve => {
            const src = script.src;
            const alreadyLoaded = Array.from(document.querySelectorAll(`script[src]`))
              .some(s => s !== script && s.src === src);
            if (alreadyLoaded) return resolve();
            script.addEventListener('load', resolve, { once: true });
            script.addEventListener('error', resolve, { once: true });
            setTimeout(resolve, 3000); // fallback por si el evento no dispara
          }))).then(runSectionCallbacks);
        }

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        requestAnimationFrame(() => {
          this.initializeJudgeMeWidgets();
          this.initializeCircleVideos();

          if (this.modalContent.classList.contains('ker-product-modal')) {
            this.initializeKerPlugins();
          }
        });

        super.show(opener);

        setTimeout(() => {
          this.initializeVideos();
        }, 100);

        document.dispatchEvent(new Event('quickAddModalOpened'));
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.extractAndHoistSectionStyles(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      // Natulim - 25-sep-26: oculta la card "Suscríbete" del widget ntl-bb-subs y
      // fuerza la selección de "Compra única" haciendo click en su card (reutiliza
      // el mismo listener/lógica que usa el widget para el toggle manual).
      forceOnetimeOnly(root) {
        root.querySelectorAll('[data-type="subscribe"]').forEach((subscribeCard) => {
          subscribeCard.style.display = 'none';
          const onetimeCard = subscribeCard.parentElement?.querySelector('[data-type="onetime"]');
          // AO-029: sin suscripción en el modal, «lo que te pierdes sin suscripción» sobra
          onetimeCard?.querySelector('[class$="__loss"]')?.style.setProperty('display', 'none');
          if (onetimeCard && !onetimeCard.classList.contains('is-selected')) {
            onetimeCard.click();
          }
        });
      }

      // Natulim TS - 08-jun-26: Extraer <style> inline de la sección y moverlos al <head>.
      // Se ejecuta DESPUÉS de preventDuplicatedIDs, por lo que los IDs en el HTML ya están
      // renombrados. Se reemplaza también #shopify-section-xxx → #quickadd-shopify-section-xxx
      // en el texto del CSS para que los selectores con ID matcheen el wrapper del modal.
      // Los <style> se marcan con data-quick-add-modal para limpiarlos al cerrar el modal.
      extractAndHoistSectionStyles(sectionElement) {
        const originalId = sectionElement.id;
        const prefixedId = originalId ? `quickadd-${originalId}` : null;

        sectionElement.querySelectorAll('style').forEach((styleTag) => {
          const hoisted = document.createElement('style');
          hoisted.setAttribute('data-quick-add-modal', '');
          let css = styleTag.textContent;
          if (originalId && prefixedId) {
            css = css.replaceAll(`#${originalId}`, `#${prefixedId}`);
          }
          hoisted.textContent = css;
          document.head.appendChild(hoisted);
          styleTag.remove();
        });
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        // Natulim (sep-2026): en gallery_layout 'stacked' y en mobile_thumbnails
        // 'peek' las imagenes son clickeables (abren el zoom de Dawn, ver
        // section-main-product.css + quick-add.css), asi que el product-modal
        // tiene que sobrevivir acá adentro tambien — antes se borraba siempre,
        // sin excepcion, y el click en una imagen del quick-add-modal no abria
        // nada.
        const productModal = productElement.querySelector('product-modal');
        const keepsModal = !!productElement.querySelector('.product--stacked, .product--mobile-peek');
        if (productModal && !keepsModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // DO NOT remove Judge.me or Recharge widgets
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        if (!sectionId) return; // secciones sin data-section (no product-info) — no hay IDs que renombrar

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);

        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;

        // Handle Judge.me widget ID prefixing
        const judgemeWidgets = productElement.querySelectorAll('[id^="judgeme_"]');
        judgemeWidgets.forEach((widget) => {
          const oldWidgetId = widget.id;
          const newWidgetId = `quickadd-${oldWidgetId}`;
          widget.id = newWidgetId;

          if (widget.innerHTML) {
            widget.innerHTML = widget.innerHTML.replaceAll(oldWidgetId, newWidgetId);
          }
        });
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeJudgeMeWidgets() {
        // Force load lazy images
        const lazyImages = this.modalContent.querySelectorAll('.jdgm-rev__pic-img[data-src]');
        lazyImages.forEach(img => {
          if (!img.src && img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
        });

        // Translate UI elements
        this.translateJudgeMeTitles();

        if (!window.jdgm) {
          setTimeout(() => this.initializeJudgeMeWidgets(), 500);
          return;
        }

        const judgemeWidgets = this.modalContent.querySelectorAll('[id^="quickadd-judgeme_"], [class*="jdgm-widget"], .jdgm-rev-widg');
        if (judgemeWidgets.length === 0) return;

        // Ensure all widgets are visible
        judgemeWidgets.forEach(widget => {
          widget.style.display = 'block';
          widget.style.visibility = 'visible';
          widget.style.opacity = '1';
        });

        let initialized = false;

        // Method 1: Try executeWidgets (most reliable)
        if (!initialized && typeof window.jdgm.executeWidgets === 'function') {
          try { window.jdgm.executeWidgets(); initialized = true; } catch (e) {}
        }

        // Method 2: Try docReady callback
        if (!initialized && typeof window.jdgm.docReady === 'function') {
          try {
            window.jdgm.docReady(() => {
              if (typeof window.jdgm.executeWidgets === 'function') window.jdgm.executeWidgets();
            });
            initialized = true;
          } catch (e) {}
        }

        // Method 3: Try renderWidgets
        if (!initialized && typeof window.jdgm.renderWidgets === 'function') {
          try { window.jdgm.renderWidgets(); initialized = true; } catch (e) {}
        }

        // Method 4: Try render with specific container
        if (!initialized && typeof window.jdgm.render === 'function') {
          try { window.jdgm.render(this.modalContent); initialized = true; } catch (e) {}
        }

        // Method 5: Dispatch custom events
        if (!initialized) {
          try {
            ['jdgm:render', 'jdgm:init', 'judgeme:render'].forEach(eventName => {
              document.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { container: this.modalContent } }));
            });
            setTimeout(() => {
              if (typeof window.jdgm.executeWidgets === 'function') window.jdgm.executeWidgets();
            }, 100);
            initialized = true;
          } catch (e) {}
        }

        // Method 6: Force attribute trigger
        if (!initialized) {
          try {
            judgemeWidgets.forEach(widget => {
              widget.setAttribute('data-jdgm-force-render', Date.now());
              void widget.offsetHeight;
            });
            if (typeof window.jdgm.executeWidgets === 'function') {
              setTimeout(() => window.jdgm.executeWidgets(), 50);
            }
          } catch (e) {}
        }
      }

      translateJudgeMeTitles() {
        const currentLang = window.Shopify?.locale || 'en';
        const translations = {
          'es': {
            'title': 'Reseñas de Clientes',
            'basedOn': 'Basado en',
            'reviews': 'reseñas',
            'otherLanguages': 'Reseñas en Otros Idiomas'
          },
          'ca': {
            'title': 'Ressenyes de Clients',
            'basedOn': 'Basat en',
            'reviews': 'ressenyes',
            'otherLanguages': 'Ressenyes en Altres Llengües'
          }
        };

        if (!translations[currentLang]) return;

        // Translate main title
        const titleElements = this.modalContent.querySelectorAll('.jdgm-rev-widg__title');
        titleElements.forEach(el => {
          el.textContent = translations[currentLang].title;
        });

        // Translate section titles
        const sectionTitles = this.modalContent.querySelectorAll('.jdgm-rev-widg__section-title');
        sectionTitles.forEach(el => {
          const text = el.textContent.trim().toLowerCase();
          if (text.includes('otros idiomas') || text.includes('altres llengües') || text.includes('other languages')) {
            el.textContent = translations[currentLang].otherLanguages;
          }
        });

        // Translate summary text
        const summaryElements = this.modalContent.querySelectorAll('.jdgm-rev-widg__summary-text');
        summaryElements.forEach(el => {
          const match = el.textContent.match(/(\d+)/);
          if (match) {
            const count = match[1];
            el.textContent = `${translations[currentLang].basedOn} ${count} ${translations[currentLang].reviews}`;
          }
        });
      }



      // Natulim SP - 4-dic-25: Inicializar plugins de KER para productos de cabello y cuerpo
      initializeKerPlugins() {
        const kerStepsContainer = this.modalContent.querySelector('.ker-steps-container');
        const kerQuantityPacksBlock = this.modalContent.querySelector('.ker-quantity-packs-block');

        // Inicializar Swiper manualmente: ker-slider-with-text usa selectores por ID (#shopify-section-xxx)
        // que quedan renombrados en el modal, por lo que el DOMContentLoaded de la sección no lo encuentra.
        this.initializeModalSwiper();

        // Si ya hay un contenedor de steps, el script inline ya se ejecutó correctamente
        if (kerStepsContainer) return;

        // Si existe el bloque pero no el container, re-ejecutar los scripts de KER
        if (kerQuantityPacksBlock) {
          this.modalContent.querySelectorAll('script').forEach(script => {
            const content = script.textContent || script.innerHTML;
            if (content.includes('KerQuantityPacksPlugin') || content.includes('KerStep')) {
              try {
                const newScript = document.createElement('script');
                newScript.textContent = content;
                this.modalContent.appendChild(newScript);
              } catch (error) {}
            }
          });
        }
      }

      // Natulim SP - 4-dic-25: Swiper para ker-slider-with-text
      // La sección usa selectores por ID (#shopify-section-xxx) que quedan renombrados en el modal.
      // Se inicializa manualmente usando querySelector por clase, sin depender del ID.
      initializeModalSwiper() {
        const swiperContainer = this.modalContent.querySelector('.slider-with-text__swiper');
        if (!swiperContainer) return;

        const init = () => {
          const prevButton = this.modalContent.querySelector('.swiper-button-prev');
          const nextButton = this.modalContent.querySelector('.swiper-button-next');
          try {
            new Swiper(swiperContainer, {
              slidesPerView: 3,
              spaceBetween: 18,
              navigation: { nextEl: nextButton, prevEl: prevButton },
              observer: true,
              observeParents: true,
              resizeObserver: true
            });
          } catch (e) {}
        };

        if (typeof Swiper !== 'undefined') {
          init();
        } else {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';
          script.onload = init;
          document.head.appendChild(script);
        }
      }

      // Natulim SP - 18-nov-25: Fix videos circulares en modal de vista rápida
      initializeCircleVideos() {
        const videoPlayer = this.modalContent.querySelectorAll(".storiesVideo");

        if (videoPlayer.length === 0) return;

        let modalWrapperVideo = document.querySelector('.modalWrapperVideo');
        let modalContainerVideos = document.querySelector('.modalContainerVideos');

        if (!modalWrapperVideo || !modalContainerVideos) {
          this.createGlobalVideoModal();
          modalWrapperVideo = document.querySelector('.modalWrapperVideo');
          modalContainerVideos = document.querySelector('.modalContainerVideos');
        }

        // Re-aplicar los event listeners
        videoPlayer.forEach(video => {
          video.pause();

          // Click listener para abrir modal de video
          video.addEventListener("click", () => {
            let bigvideosrc = video.dataset.video;

            // Usar la lógica existente
            modalContainerVideos.querySelectorAll(`video`).forEach(bigvideos => {
              bigvideos.pause();
              bigvideos.classList.add("hidden");
            });

            let searchexist = modalContainerVideos.querySelector(`video[src="${bigvideosrc}"]`);
            if (Boolean(searchexist)) {
              searchexist.play();
              searchexist.classList.remove("hidden");
              modalWrapperVideo.classList.remove("hidden");
            } else {
              const bigVideo = document.createElement("video");
              bigVideo.src = bigvideosrc;
              bigVideo.loop = true;
              modalContainerVideos.appendChild(bigVideo);
              modalWrapperVideo.classList.remove("hidden");
              bigVideo.play();
            }
          });

          // Ended listener para auto-play siguiente
          video.addEventListener("ended", () => {
            if (typeof window.playNextVideo === 'function') {
              window.playNextVideo();
            } else {
              // Fallback simple si la función global no existe
              this.playNextVideoFallback(videoPlayer, video);
            }
          });
        });

      }

      // Natulim SP - 18-nov-25: Fix videos circulares en modal de vista rápida
      // Crea el modal global de videos si no existe, replicando la funcionalidad de main-product.liquid
      createGlobalVideoModal() {
        // Crear el modal wrapper principal
        const modalWrapperVideo = document.createElement("div");
        modalWrapperVideo.classList.add("modalWrapperVideo", "hidden");

        // Crear el contenedor de videos
        const modalContainerVideos = document.createElement("div");
        modalContainerVideos.classList.add("modalContainerVideos");

        // Crear botón de cerrar
        const closeModalVideo = document.createElement("div");
        closeModalVideo.classList.add("closeModalVideo");
        closeModalVideo.innerHTML = '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.055 16.594a.662.662 0 0 1-.899 0L10 11.398l-5.195 5.196a.662.662 0 0 1-.899 0 .662.662 0 0 1 0-.899L9.102 10.5 3.906 5.344a.662.662 0 0 1 0-.899.662.662 0 0 1 .899 0L10 9.641l5.156-5.196a.662.662 0 0 1 .899 0 .662.662 0 0 1 0 .899L10.859 10.5l5.196 5.195a.662.662 0 0 1 0 .899Z" style="fill: #FFFFFF !important;"></path></svg>';

        // Event listener para cerrar
        closeModalVideo.addEventListener("click", e => {
          modalContainerVideos.querySelectorAll("video").forEach(vid => vid.pause());
          modalWrapperVideo.classList.add("hidden");
        });

        // Obtener información del producto desde el modal
        const productInfo = this.modalContent.querySelector('product-info');
        const productTitle = productInfo?.querySelector('h1, h2')?.textContent || 'Producto';
        const productImage = productInfo?.querySelector('.product__media img')?.src || '';
        const productPrice = productInfo?.querySelector('.price')?.textContent || '';

        // Crear wrapper del producto
        const productWrapperModalVideo = document.createElement("div");
        productWrapperModalVideo.innerHTML = `
        <div class="flex videoButtons">
          <button class="toggleBoxProduct" onclick="window.toggleBoxProduct && window.toggleBoxProduct(this)">
            <span>Ver producto</span>
            <svg width="24" height="24" viewBox="0 0 44.308 44.308" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M31.767 20.849 16.727 5.809a0.923 0.923 0 0 0 -1.305 0l-1.305 1.305a0.923 0.923 0 0 0 0 1.305L27.851 22.154l-13.735 13.734a0.923 0.923 0 0 0 0 1.305l1.306 1.305a0.923 0.923 0 0 0 1.305 0l15.04 -15.04a1.846 1.846 0 0 0 0 -2.61Z"></path></svg>
          </button>
          <button class="toggleSound" onclick="window.toggleSound && window.toggleSound(this)">
            <svg class="soundDisable" width="20" height="20" viewBox="0 0 24 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.508.867C12.313.79 12.156.79 12 .79c-.313 0-.625.078-.86.313L5.868 5.75H2.625C1.57 5.75.75 6.61.75 7.625v3.71c0 1.017.82 1.837 1.875 1.837h3.242l5.274 4.687c.234.196.546.313.859.313.156 0 .313-.04.508-.117.43-.196.703-.664.703-1.133V2c0-.469-.274-.938-.703-1.133Zm-1.133 14.766-4.766-4.258H2.625v-3.71h3.984l4.766-4.259v12.227ZM20.789 9.5l1.836-1.836c.39-.39.39-.976 0-1.328-.352-.39-.938-.39-1.328 0L19.5 8.21l-1.875-1.875c-.39-.352-.977-.352-1.328 0-.39.39-.39.976 0 1.328L18.133 9.5l-1.836 1.836c-.39.39-.39.976 0 1.328.351.39.937.39 1.328 0l1.875-1.836 1.836 1.836c.352.39.937.39 1.289 0 .39-.351.39-.937 0-1.328L20.789 9.5Z" fill="#fff"></path></svg>
            <svg class="soundEnable" width="20" height="17" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.758.867A1.349 1.349 0 0 0 11.25.75c-.313 0-.625.117-.86.352L5.118 5.75H1.875C.82 5.75 0 6.61 0 7.625v3.71c0 1.017.82 1.837 1.875 1.837h3.242l5.274 4.687c.234.313.546.391.859.391.156 0 .313 0 .508-.078.43-.235.742-.664.742-1.172V2c0-.469-.313-.938-.742-1.133Zm-1.133 14.766-4.766-4.258H1.875v-3.71h3.984l4.766-4.259v12.227Zm5.469-9.024a1.053 1.053 0 0 0-.586-.195.962.962 0 0 0-.742.352.942.942 0 0 0 .156 1.328c.43.351.703.86.703 1.406a1.85 1.85 0 0 1-.703 1.445.942.942 0 0 0-.156 1.328.962.962 0 0 0 .742.352c.195 0 .43-.078.586-.195.898-.742 1.406-1.797 1.406-2.93 0-1.094-.508-2.148-1.406-2.89Zm2.383-2.89c-.196-.117-.391-.196-.625-.196-.274 0-.508.118-.704.352-.351.39-.273.977.118 1.328 1.328 1.055 2.109 2.656 2.109 4.297 0 1.68-.781 3.281-2.11 4.336-.39.351-.468.937-.117 1.328.196.234.43.352.704.352.234 0 .43-.079.625-.196 1.757-1.445 2.773-3.554 2.773-5.82 0-2.227-1.016-4.336-2.773-5.781Z" fill="#fff"></path></svg>
          </button>
        </div>
        <div class="productBoxModal">
          <img src="${productImage}"/>
          <div class="productBoxModalText">
            <h3>${productTitle}</h3>
            <div class="modalBoxAddtoCart">
              <span>${productPrice}</span>
              <button onclick="window.addToCart && window.addToCart()">Añadir al carrito</button>
            </div>
          </div>
        </div>`;
        productWrapperModalVideo.classList.add("productWrapperModalVideo");

        // Crear botón play/pause
        const playPauseVideo = document.createElement("div");
        playPauseVideo.innerHTML = `
          <svg width="90" height="90" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50.387 85.167c-3.142 0-5.387-2.47-5.387-5.387V40.275C45 37.469 47.245 35 50.387 35c.898 0 1.908.224 2.806.786l32.322 19.752c1.571.898 2.581 2.694 2.581 4.49 0 1.907-1.01 3.703-2.581 4.6L53.193 84.382a5.33 5.33 0 0 1-2.806.786Z" fill="#fff"></path></svg>
        `;
        playPauseVideo.classList.add("playPauseVideo");
        playPauseVideo.addEventListener("click", e => {
          playPauseVideo.classList.toggle("pauseVideo");
          modalWrapperVideo.querySelectorAll('video').forEach(video => {
            if (playPauseVideo.classList.contains("pauseVideo")) {
              video.pause();
            } else {
              modalContainerVideos.querySelector(`video:not(.hidden)`).play();
            }
          });
        });

        // Ensamblar el modal
        modalWrapperVideo.appendChild(closeModalVideo);
        modalWrapperVideo.appendChild(modalContainerVideos);
        modalWrapperVideo.appendChild(productWrapperModalVideo);
        modalWrapperVideo.appendChild(playPauseVideo);

        // Agregar al DOM
        document.body.appendChild(modalWrapperVideo);

        // Crear funciones globales si no existen
        if (!window.toggleBoxProduct) {
          window.toggleBoxProduct = function (pelem) {
            pelem.closest('.productWrapperModalVideo').classList.toggle("hideProductBoxModal");
          }
        }

        if (!window.toggleSound) {
          window.toggleSound = function (pelem) {
            pelem.closest('.productWrapperModalVideo').classList.toggle("disableSound");
            pelem.classList.toggle("disableSoundBtn");
            pelem.closest('.modalWrapperVideo').querySelectorAll("video").forEach(video => {
              if (pelem.classList.contains("disableSoundBtn")) {
                video.muted = true;
              } else {
                video.muted = false;
              }
            });
          }
        }

        if (!window.addToCart) {
          window.addToCart = function () {
            // Find the add to cart button in the modal and click it
            const modal = document.querySelector('quick-add-modal');
            const addToCartBtn = modal?.querySelector('button[name="add"], .btn.product-form__cart-submit');
            if (addToCartBtn) {
              addToCartBtn.click();
            }
          }
        }

      }

      // Natulim SP - 19-nov-25: Fix autoplay videos en modal de vista rápida
      // Basado en la solución que funciona en natulim-ro\assets\quick-add-modal.js
      initializeVideos() {
        // Reproducir solo los videos con atributo autoplay (no los videos circulares)
        const autoplayVideos = this.modalContent.querySelectorAll('video[autoplay]');
        autoplayVideos.forEach(video => {
          if (video.muted) {
            // Solo reproducir si está muted (política de navegadores)
            video.play().catch(() => {});
          }
        });
      }

      // Natulim SP - 18-nov-25: Fix videos circulares en modal de vista rápida
      playNextVideoFallback(videoPlayer, currentVideo) {
        const currentIndex = Array.from(videoPlayer).indexOf(currentVideo);
        const nextIndex = (currentIndex + 1) % videoPlayer.length;
        const nextVideo = videoPlayer[nextIndex];

        if (nextVideo && nextVideo.dataset.src) {
          if (!nextVideo.src) {
            nextVideo.src = nextVideo.dataset.src;
          }
          nextVideo.play();
        }
      }

    }
  );
}